/**
 * Turns the public `baseUrl` / `ledgerBaseUrl` options into the consent server
 * and ledger URLs the API functions take.
 *
 * Host mode: `baseUrl` is the API host (`https://api.redacto.io`). The consent
 * server is `{host}/consent` and the ledger `{host}/consent-ledger`; a
 * `ledgerBaseUrl` passed alongside the host is ignored.
 *
 * Legacy mode: `baseUrl` is omitted, is the consent server URL (`…/consent`)
 * earlier releases documented, or is not an absolute URL. Both values pass
 * through untouched, so every request is the one earlier releases sent,
 * including the default host when `baseUrl` is omitted.
 */

const CONSENT_SERVICE_PATH = "/consent";
const LEDGER_SERVICE_PATH = "/consent-ledger";

export type EndpointMode = "host" | "legacy";

export type ResolvedEndpoints =
  | { mode: "host"; baseUrl: string; ledgerBaseUrl: string }
  | { mode: "legacy"; baseUrl: string | undefined; ledgerBaseUrl: string | undefined };

/**
 * An absolute http(s) URL: scheme, authority, optional path. A string match
 * rather than `new URL(...)` so the check is the same in every runtime.
 */
const ABSOLUTE_HTTP_URL = /^https?:\/\/[^\s/?#]+(?:\/[^\s?#]*)?$/i;

const withoutTrailingSlash = (value: string): string => value.trim().replace(/\/+$/, "");

export const resolveEndpoints = (
  baseUrl?: string,
  ledgerBaseUrl?: string
): ResolvedEndpoints => {
  const trimmed = withoutTrailingSlash(baseUrl ?? "");
  if (!ABSOLUTE_HTTP_URL.test(trimmed) || trimmed.endsWith(CONSENT_SERVICE_PATH)) {
    return { mode: "legacy", baseUrl, ledgerBaseUrl };
  }

  const host = trimmed.endsWith(LEDGER_SERVICE_PATH)
    ? trimmed.slice(0, -LEDGER_SERVICE_PATH.length)
    : trimmed;
  return {
    mode: "host",
    baseUrl: `${host}${CONSENT_SERVICE_PATH}`,
    ledgerBaseUrl: `${host}${LEDGER_SERVICE_PATH}`,
  };
};

const DEPRECATION_WARNINGS = {
  omittedBaseUrl:
    "[Redacto] No baseUrl was passed, so requests go to the built-in production API. Pass baseUrl, the Redacto API host for your environment.",
  consentServerBaseUrl:
    "[Redacto] A baseUrl ending in /consent is deprecated. Pass the Redacto API host instead; the SDK reaches the consent server and the ledger under it.",
  ledgerBaseUrl:
    "[Redacto] ledgerBaseUrl is deprecated. Pass the Redacto API host as baseUrl instead; the SDK then reaches the consent server and the ledger under it.",
  ledgerIgnored:
    "[Redacto] ledgerBaseUrl is ignored because baseUrl is the API host; the SDK reaches the ledger under that host. Remove ledgerBaseUrl.",
  privacyCenterSlug:
    "[Redacto] slug is deprecated. Pass organisationUuid to privacyCenter() instead; slug will be removed in a future major release.",
} as const;

type DeprecationWarning = keyof typeof DEPRECATION_WARNINGS;

const issuedWarnings = new Set<DeprecationWarning>();

const warnOnce = (warning: DeprecationWarning): void => {
  if (issuedWarnings.has(warning)) return;
  issuedWarnings.add(warning);
  try {
    console.warn(DEPRECATION_WARNINGS[warning]);
  } catch {
    // A host page that stubs out the console must not break the notice.
  }
};

/** Reports, once per page load, that the Privacy Center was given a slug. */
export const warnDeprecatedPrivacyCenterSlug = (slug?: string): void => {
  if (slug?.trim()) warnOnce("privacyCenterSlug");
};

/**
 * {@link resolveEndpoints} for an entry point's public options, reporting a
 * deprecated configuration once per kind on the console. Never throws.
 */
export const resolveEndpointOptions = (
  baseUrl?: string,
  ledgerBaseUrl?: string
): ResolvedEndpoints => {
  // Only an omitted or empty baseUrl falls back to the default host.
  if (!baseUrl) warnOnce("omittedBaseUrl");
  else if (withoutTrailingSlash(baseUrl).endsWith(CONSENT_SERVICE_PATH)) {
    warnOnce("consentServerBaseUrl");
  }
  const resolved = resolveEndpoints(baseUrl, ledgerBaseUrl);
  if (ledgerBaseUrl?.trim()) {
    warnOnce(resolved.mode === "host" ? "ledgerIgnored" : "ledgerBaseUrl");
  }
  return resolved;
};
